/** Replay simulator (S2): scrub through an archived session and read the
 * chain as it stood.
 *
 * No positions yet — that is S3. What this buys on its own is the ability
 * to go back to any captured minute and see exactly what the market looked
 * like, which nothing else in the app can do.
 *
 * ONE REQUEST PER STEP, deliberately. Measured before building: ~25ms
 * round-trip on a kept-alive connection plus a sub-millisecond indexed
 * read, against the ~100ms where an interaction stops feeling instant.
 * Prefetching neighbouring minutes would buy nothing at that latency and
 * cost real complexity.
 */

import { useCallback, useState, type ReactElement } from "react";
import { describeError } from "@/lib/http";
import type { SimChain, SimPayoff, SimSessions } from "@/lib/simulatorApi";
import { SimulatorControls, ExpiryTabs } from "./SimulatorControls";
import { ChainHeader } from "./ChainHeader";
import { ChainTable } from "./ChainTable";
import { useSimulatorState, type SimulatorState } from "./useSimulatorState";
import { usePositions, lotsByContract } from "./usePositions";
import { useAutoRun } from "./useAutoRun";
import { MtmChart } from "./MtmChart";
import { SavedBaskets } from "./SavedBaskets";
import { useLoadSaved } from "./useLoadSaved";
import { StrategyLibrary } from "./StrategyLibrary";
import { SwitchIndexDialog } from "./SwitchIndexDialog";
import { useFitToViewport } from "./useFitToViewport";
import { PositionsPanel } from "./PositionsPanel";
import { PayoffChart } from "@/components/Strategies/PayoffChart";
import { toPayoffView } from "./payoffView";
import {
  SESSION_OPEN,
  atSessionLimit,
  clampTo,
  earliestEntry,
  planStep,
} from "@/lib/simulatorClock";

/** The right-hand column: the basket if there is one, otherwise the
 * fastest way to get one.
 *
 * ⭐ THE PAYOFF BELONGS BESIDE THE CHAIN, NOT BELOW IT. Stacked, every
 * clock step meant scrolling to the bottom to read the result and back up
 * to step again — which is most of the work in a replay, done twice per
 * minute. Side by side, the chain and what it produces are visible at once
 * and the loop closes without moving.
 */
/** Booking a leg uses the price ON SCREEN, at the minute on screen — the
 * same rule entries follow. Re-deriving it server-side would book a price
 * the reader never saw, and a booked number is the one thing here that
 * must match what was in front of them when they pressed the button.
 *
 * A leg the archive cannot price at this minute is left OPEN rather than
 * closed at a guess: a fabricated exit is a fabricated result.
 */
function useCloseHandlers(
  positions: ReturnType<typeof usePositions>,
  payoff: SimPayoff | undefined,
  at: string,
): { one: (index: number) => void; all: () => void } {
  const { closeAt, closeAll } = positions;
  const one = useCallback(
    (index: number) => {
      const ltp = payoff?.legs[index]?.ltp;
      if (ltp !== null && ltp !== undefined) closeAt(index, at, ltp);
    },
    [closeAt, payoff, at],
  );
  const all = useCallback(() => {
    closeAll(at, (leg) => {
      const priced = payoff?.legs.find(
        (l) => l.strike === leg.strike && l.side === leg.side && l.entry === leg.entry,
      );
      return priced?.ltp ?? null;
    });
  }, [closeAll, payoff, at]);
  return { one, all };
}

/** The panel takes nine props; bundling them here keeps `BasketOrLibrary`
 * inside the line limit without hiding what it is given. */
function panelProps(
  positions: ReturnType<typeof usePositions>,
  payoff: SimPayoff,
  at: string,
  closing: { one: (i: number) => void; all: () => void },
): React.ComponentProps<typeof PositionsPanel> {
  return {
    payoff,
    at,
    onClose: closing.one,
    onReopen: positions.reopen,
    onMark: (index, price) => {
      positions.setMark(index, at, price);
    },
    onExit: positions.setExit,
    onResetMark: positions.clearMark,
    onCloseAll: closing.all,
    multiplier: positions.multiplier,
    maxMultiplier: positions.maxMultiplier,
    onMultiplier: positions.setMultiplier,
    onRemove: positions.removeAt,
    onLots: positions.setLots,
    onEntry: positions.setEntry,
    onClear: positions.clear,
  };
}

function BasketOrLibrary({
  chain,
  positions,
  at,
  onExpiry,
}: Readonly<{
  chain: SimChain;
  positions: ReturnType<typeof usePositions>;
  at: string;
  onExpiry: (v: string) => void;
}>): ReactElement {
  const payoff = positions.payoff.data;
  // Before any early return: hook order cannot depend on whether the
  // basket happens to be empty.
  const closing = useCloseHandlers(positions, payoff, at);

  if (positions.legs.length === 0) {
    return (
      <StrategyLibrary
        rows={chain.rows}
        atm={chain.atm_strike}
        at={at}
        onBuild={positions.replaceAll}
        expiry={chain.expiry}
        expiries={chain.expiries}
        onExpiry={onExpiry}
      />
    );
  }
  if (!payoff) {
    return <p className="text-sm text-ink-secondary">Pricing the basket…</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      <PositionsPanel {...panelProps(positions, payoff, at, closing)} />
      {/* Payoff first: it answers "what is this structure", which is the
          question you have while still building. The session curve answers
          "how did it go", which only becomes interesting once the shape is
          settled and the clock is moving. */}
      <PayoffChart data={toPayoffView(payoff)} />
      {positions.mtm.data && <MtmChart mtm={positions.mtm.data} at={at} />}
    </div>
  );
}

/** The chain and whatever basket has been built off it. Split from
 * `Replay` only to keep each function inside the line limit — they are
 * one screen. */
/** Switching index empties the basket, after asking.
 *
 * Operator, 2026-08-29: *"On symbol change should clear"*, then *"Should
 * get confirmation dialog"*. A NIFTY 24100 CE is not a strike SENSEX
 * lists, so the basket cannot survive the move — but losing a structure
 * to one stray click on a dropdown, with no undo, is worth a question.
 *
 * ⚠️ ASKED ONLY WHEN THERE IS SOMETHING TO LOSE. A confirmation on an
 * empty basket always says yes, which teaches people to dismiss the one
 * that matters without reading it.
 *
 * ⚠️ AND NOT ON THE LOAD PATH. `useLoadSaved` switches index as part of
 * opening a saved basket and calls `replaceAll` straight after; prompting
 * there would ask whether to discard a basket the reader is in the middle
 * of replacing. It uses the raw `pickIndex`, deliberately.
 */
function useIndexSwitch(
  pickIndex: (next: string) => void,
  clear: () => void,
  legCount: number,
): {
  request: (next: string) => void;
  pending: string | null;
  confirm: () => void;
  cancel: () => void;
} {
  const [pending, setPending] = useState<string | null>(null);

  const request = useCallback(
    (next: string) => {
      if (legCount === 0) {
        pickIndex(next);
        return;
      }
      setPending(next);
    },
    [legCount, pickIndex],
  );

  const confirm = useCallback(() => {
    if (pending === null) return;
    // Clear FIRST, then switch: both are state updates in one handler, and
    // this order leaves no render in which the old basket is priced
    // against the new index's chain.
    clear();
    pickIndex(pending);
    setPending(null);
  }, [pending, clear, pickIndex]);

  const cancel = useCallback(() => {
    setPending(null);
  }, []);

  return { request, pending, confirm, cancel };
}

function Controls({
  state,
  indices,
  underlying,
  tradeDate,
  floor,
  onUnderlying,
  autoRunning,
  onAutoRun,
}: Readonly<{
  state: SimulatorState;
  indices: SimSessions[];
  underlying: string;
  tradeDate: string;
  floor: string;
  onUnderlying: (next: string) => void;
  autoRunning: boolean;
  onAutoRun: () => void;
}>): ReactElement {
  return (
    <SimulatorControls
      indices={indices}
      underlying={underlying}
      tradeDate={tradeDate}
      sessions={state.sessions}
      at={state.at}
      floor={floor}
      onUnderlying={onUnderlying}
      onTradeDate={state.pickDate}
      onAt={(v) => {
        state.setAt(clampTo(v, floor));
      }}
      onStep={(m) => {
        // The clock runs CONTINUOUSLY across sessions: past 15:29 it rolls
        // into the next captured day at 09:16, and back past 09:16 into the
        // previous one at 15:29. `planStep` decides; the day and the clock
        // live in different hooks, so both are applied here.
        const plan = planStep(state.at, m, floor, {
          hasPrev: !atSessionLimit(state.sessions, tradeDate, -1),
          hasNext: !atSessionLimit(state.sessions, tradeDate, 1),
        });
        if (plan.dayDelta !== 0) state.stepDay(plan.dayDelta);
        state.setAt(plan.at);
      }}
      onStepDay={state.stepDay}
      autoRunning={autoRunning}
      onAutoRun={onAutoRun}
    />
  );
}

function Board({
  chain,
  positions,
  at,
  onExpiry,
  loading,
}: Readonly<{
  chain: SimChain;
  positions: ReturnType<typeof usePositions>;
  at: string;
  onExpiry: (v: string) => void;
  loading: boolean;
}>): ReactElement {
  return (
    <>
      <ChainHeader chain={chain} loading={loading} />
      {/* Each pane scrolls on its own; the page itself must not. */}
      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-2">
        <div
          className={`flex min-h-0 flex-col gap-1.5 transition-opacity ${
            loading ? "opacity-50" : ""
          }`}
        >
          <ExpiryTabs expiry={chain.expiry} expiries={chain.expiries} onExpiry={onExpiry} />
          <ChainTable
            rows={chain.rows}
            atm={chain.atm_strike}
            expiries={chain.expiries.length}
            onTrade={positions.add}
            onRemove={positions.removeContract}
            lots={lotsByContract(positions.legs)}
            at={at}
          />
        </div>
        <div className="scroll-slim min-h-0 overflow-auto pr-1">
          <BasketOrLibrary chain={chain} positions={positions} at={at} onExpiry={onExpiry} />
        </div>
      </div>
    </>
  );
}

/** The chain, its error, or nothing yet — kept apart from `Replay` so that
 * function stays inside the line limit. */
function ChainArea({
  chain,
  positions,
  at,
  onExpiry,
}: Readonly<{
  chain: SimulatorState["chain"];
  positions: ReturnType<typeof usePositions>;
  at: string;
  onExpiry: (v: string) => void;
}>): ReactElement {
  return (
    <>
      {chain.isError && <ChainError error={chain.error} />}
      {chain.data && (
        <Board
          chain={chain.data}
          positions={positions}
          at={at}
          onExpiry={onExpiry}
          loading={chain.isFetching}
        />
      )}
    </>
  );
}

function ChainError({ error }: Readonly<{ error: unknown }>): ReactElement {
  return (
    <p className="text-sm text-danger">No chain for that combination: {describeError(error)}</p>
  );
}

/** Reopening a saved basket needs four of the page's setters at once, so
 * it is bundled here rather than spelled out inside `Replay`. */
function useReopen(
  state: SimulatorState,
  replaceAll: ReturnType<typeof usePositions>["replaceAll"],
  stopAutoRun: () => void,
): ReturnType<typeof useLoadSaved> {
  return useLoadSaved({
    // Raw `pickIndex` on purpose — see `useIndexSwitch`: prompting here
    // would ask whether to discard a basket being replaced.
    pickIndex: state.pickIndex,
    pickDate: state.pickDate,
    setExpiry: state.setExpiry,
    replaceAll,
    stopAutoRun,
  });
}

function Replay({
  state,
  indices,
  underlying,
  tradeDate,
}: Readonly<{
  state: SimulatorState;
  indices: SimSessions[];
  underlying: string;
  tradeDate: string;
}>): ReactElement {
  const { chain } = state;
  const expiry = chain.data?.expiry ?? state.expiry;
  const positions = usePositions({ underlying, tradeDate, at: state.at, expiry });
  const auto = useAutoRun(state.at, state.stepBy);
  const fit = useFitToViewport();
  const floor = earliestEntry(positions.legs) ?? SESSION_OPEN;
  const switching = useIndexSwitch(state.pickIndex, positions.clear, positions.legs.length);
  const load = useReopen(state, positions.replaceAll, auto.stop);

  return (
    <section ref={fit.ref} style={{ height: fit.height }} className="card flex flex-col gap-4">
      <Controls
        state={state}
        indices={indices}
        underlying={underlying}
        tradeDate={tradeDate}
        floor={floor}
        onUnderlying={switching.request}
        autoRunning={auto.running}
        onAutoRun={auto.toggle}
      />
      <ChainArea chain={chain} positions={positions} at={state.at} onExpiry={state.setExpiry} />
      <SwitchIndexDialog {...switching} legCount={positions.legs.length} />
      <SavedBaskets
        underlying={underlying}
        tradeDate={tradeDate}
        expiry={expiry}
        legs={positions.legs}
        onLoad={load}
      />
    </section>
  );
}

export function SimulatorPage(): ReactElement {
  const state = useSimulatorState();
  const { indices, underlying, tradeDate } = state;
  const ready = indices.data && underlying !== null && tradeDate !== null;
  return (
    <main className="mx-auto max-w-[110rem] px-6 py-4">
      {indices.isPending && <p className="text-sm text-ink-secondary">Loading sessions…</p>}
      {indices.isError && (
        <p className="text-sm text-danger">
          Failed to load sessions: {describeError(indices.error)}
        </p>
      )}
      {ready && (
        <Replay
          state={state}
          indices={indices.data}
          underlying={underlying}
          tradeDate={tradeDate}
        />
      )}
    </main>
  );
}
